import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { Box, IconButton, styled } from '@mui/material';
import { m as lazyMotion, type Variants } from 'motion/react';
import { Link as RouterLink } from 'react-router';

import { useStartRefinFlow } from '@/hooks/useStartRefinFlow.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import { Button } from '@/ui/button-2.tsx';

import { routes } from '../../assets/constants/routes-no.ts';
import BanksIcon from '../../assets/images/myEconomy/banks.svg?react';
import CheapestLoanIcon from '../../assets/images/myEconomy/cheapest_loan.svg?react';
import CreditCheckMyselfIcon from '../../assets/images/myEconomy/credit_check.svg?react';
import DebtInformationIcon from '../../assets/images/myEconomy/debt_information.svg?react';
import DebtRegisterIcon from '../../assets/images/myEconomy/debt_register.svg?react';
import LoanIcon from '../../assets/images/myEconomy/loan.svg?react';
import MyDebtIcon from '../../assets/images/myEconomy/my_debt.svg?react';
import { palette } from '../../assets/styles/palette.ts';
import { Breadcrumbs } from '../../components/Breadcrumbs/index.ts';
import { LANG } from '../../services/Config.js';
import { ArticleCard } from '../articles/-components/article-card.tsx';
import { getArticles } from '../articles/registry.ts';
import { ARTICLES_INDEX_PATH } from '../articles/tags.ts';

const CardsListContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '100%',
  maxWidth: 700,
  flexDirection: 'column',

  [theme.breakpoints.up(850)]: {
    maxWidth: 'unset',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },

  [theme.breakpoints.up('lg')]: {
    maxWidth: 1100,
  },
}));

const CardContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  wordBreak: 'break-word',
  width: '100%',
  padding: theme.spacing(1.5),
  [theme.breakpoints.up(300)]: {
    padding: theme.spacing(1.5, 2),
    wordBreak: 'unset',
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5, 4),
  },
  '& > *': { zIndex: 1 },
}));

const CardLink = styled(RouterLink)(({ theme }) => ({
  width: '100%',
  textDecoration: 'none',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up(850)]: {
    width: '49%',
  },
}));

const StyledMotionDiv = styled(lazyMotion.div)(({ theme }) => ({
  boxShadow: theme.shadows[21],
  color: theme.palette.text.default,
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  minHeight: 80,
  [theme.breakpoints.up('sm')]: {
    height: 120,
  },
}));

const StyledMotionSpan = styled(lazyMotion.span)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  borderRadius: theme.shape.borderRadius,
}));

const Icon = styled('div')(({ theme }) => ({
  width: 40,
  minWidth: 40,
  height: 40,
  marginRight: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    width: 56,
    minWidth: 56,
    height: 56,
    marginRight: theme.spacing(3),
  },
}));

const ArrowButton = styled(IconButton)(({ theme }) => ({
  marginLeft: 'auto',
  marginRight: theme.spacing(-1.5),
}));

const OpenChatTitle = styled('h3')(({ theme }) => ({
  maxWidth: 700,
  textAlign: 'center',
  marginTop: theme.spacing(8),
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(16),
  },
}));

const variants = {
  hover: {
    clipPath: `circle(1500px at 60px 60px)`,
    backgroundColor: palette.secondary.main,
    transition: {
      type: 'spring',
      stiffness: 20,
      restDelta: 2,
    },
  },
  closed: {
    clipPath: `circle(0px at 60px 60px)`,
    backgroundColor: 'transparent',
    transition: {
      ease: 'easeIn',
      type: 'spring',
      stiffness: 100,
    },
  },
} satisfies Variants;

const ECONOMY_PAGES = [
  { name: 'MY_DEBT', icon: MyDebtIcon },
  { name: 'CREDIT_CHECK_MYSELF', icon: CreditCheckMyselfIcon },
  // { name: 'PARTNER_BANKS', icon: BanksIcon },
  { name: 'DEBT_REGISTER', icon: DebtRegisterIcon },
  { name: 'DEBT_INFORMATION', icon: DebtInformationIcon },
  { name: 'INTEREST_RATE', icon: LoanIcon },
  { name: 'SMALL_LOAN', icon: CheapestLoanIcon },
  { name: 'DEBT_FREE', icon: CheapestLoanIcon },
  { name: 'CREDIT_CARD_LOAN', icon: CheapestLoanIcon },
  { name: 'DEBT_OVERVIEW', icon: CheapestLoanIcon },
  { name: 'REPAYMENT_PERIOD', icon: CheapestLoanIcon },
  { name: 'COMPARE_LOANS', icon: CheapestLoanIcon },
  { name: 'REFINANCING_LOANS', icon: CheapestLoanIcon },
  { name: 'COMPARE_CONSUMER_LOANS', icon: CheapestLoanIcon },
  { name: 'SAVE_OR_REPAY_LOAN', icon: CheapestLoanIcon },
  { name: 'MICROLOAN', icon: CheapestLoanIcon },
  { name: 'NOMINAL_AND_EFFECTIVE_INTEREST_RATE', icon: CheapestLoanIcon },
  { name: 'PROPERTY_VALUE', icon: BanksIcon },
] as const;

const FEATURED_ARTICLE_COUNT = 2;

const MyEconomy = () => {
  const { appStrings } = LANG;
  const startRefinFlow = useStartRefinFlow();
  const featuredArticles = getArticles().slice(0, FEATURED_ARTICLE_COUNT);

  return (
    <div className="w-full bg-white py-4 pb-16 sm:py-8 xl:py-16">
      <title>{appStrings.MENU.MY_ECONOMY}</title>

      <article className="mx-auto flex max-w-6xl flex-col gap-8 px-4">
        <Breadcrumbs items={[{ text: 'Hjem', url: '/' }, { text: appStrings.MENU.MY_ECONOMY }]} />
        <h1 className="typography-v1-display">{appStrings.MENU.MY_ECONOMY}</h1>

        {featuredArticles.length > 0 && (
          <div className="mx-auto mb-8 flex w-full max-w-275 flex-col gap-8">
            <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {featuredArticles.map((article) => (
                <li key={article.slug}>
                  <ArticleCard meta={article} source="my_economy" />
                </li>
              ))}
            </ul>
            <RouterLink
              to={ARTICLES_INDEX_PATH}
              className="text-center typography-v1-body2 font-semibold text-v1-primary-100 underline"
              onClick={() => {
                sendUserPortalEvent('my_economy_see_all_articles_clicked');
              }}
            >
              Se alle artikler
            </RouterLink>
          </div>
        )}

        <CardsListContainer>
          {ECONOMY_PAGES.map(({ name, icon: IconComponent }) => (
            <CardLink
              key={name}
              to={routes[name]}
              onClick={() => {
                sendUserPortalEvent('my_economy_topic_clicked', { topic: name });
              }}
            >
              <StyledMotionDiv initial="closed" whileHover="hover">
                <StyledMotionSpan variants={variants} />
                <CardContent>
                  <Icon as={IconComponent} />
                  <p className="typography-v1-legacy-p1SemiBold">{appStrings.MENU[name]}</p>
                  <ArrowButton size="large">
                    <KeyboardArrowRightRoundedIcon />
                  </ArrowButton>
                </CardContent>
              </StyledMotionDiv>
            </CardLink>
          ))}
        </CardsListContainer>

        <OpenChatTitle className="mx-auto! typography-v1-legacy-h3">
          Se om du kan få en bedre deal på forbrukslån eller refinansiering.
        </OpenChatTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 6,
          }}
        >
          <Button
            color="yellow"
            onClick={() => {
              sendUserPortalEvent('my_economy_cta_clicked');
              startRefinFlow();
            }}
          >
            Sjekk hvor mye du kan spare
          </Button>
        </Box>
      </article>
    </div>
  );
};

export default MyEconomy;
